import React, {useState} from "react";
import styled from "styled-components";
import {Link} from "react-router-dom";
import {BoardContainer, TitleContainer} from "../../assets/GlobalStyles";

const NOTION_GUIDE_URL = "https://www.notion.so/API-2afbe20d845e8002a124c20d307295da?source=copy_link";

const VAST_EXAMPLE = `<VAST version="4.1">
  <Ad id="12345">
    <InLine>
      <AdSystem>MyAdServer v1.0</AdSystem>
      <AdTitle>Summer Campaign 2025</AdTitle>
      <Impression><![CDATA[https://track.example.com/imp?id=12345]]></Impression>
      <Creatives>
        <Creative>
          <Linear>
            <Duration>00:00:30</Duration>
            <MediaFiles>
              <MediaFile type="audio/mpeg" bitrate="160" delivery="progressive">
                <![CDATA[https://cdn.example.com/ad.mp3]]>
              </MediaFile>
            </MediaFiles>
            <TrackingEvents>
              <Tracking event="start"><![CDATA[https://track.example.com/start?id=12345]]></Tracking>
              <Tracking event="midpoint"><![CDATA[https://track.example.com/mid?id=12345]]></Tracking>
              <Tracking event="complete"><![CDATA[https://track.example.com/complete?id=12345]]></Tracking>
            </TrackingEvents>
            <VideoClicks>
              <ClickThrough><![CDATA[https://brand.example.com/landing]]></ClickThrough>
            </VideoClicks>
          </Linear>
        </Creative>
      </Creatives>
      <CompanionAds>
        <Companion width="300" height="250"><!-- 배너 리소스 --></Companion>
      </CompanionAds>
    </InLine>
  </Ad>
</VAST>`;

const VAST_NOAD_EXAMPLE = `<VAST version="4.1">
  <!-- 광고 없음: <Ad> 노드가 포함되지 않습니다 -->
</VAST>`;

const JSON_EXAMPLE = `{
  "response": "ad",
  "ads": [
    {
      "duration": "00:00:30",
      "impression": "https://track.example.com/imp?id=12345",
      "mediaFile": "https://cdn.example.com/ad.mp3"
    }
  ]
}`;

const JSON_NOAD_EXAMPLE = `{
  "response": "noAd",
  "ads": []
}`;

const REQUEST_PARAMS = [
  {name: "mediaScriptNo", type: "String", desc: "지면 번호", required: true, defaultValue: "-", example: "49003821-554a-437a-b1eb-1512a40570d1"},
  {name: "output", type: "Enum", desc: "응답 형식", required: true, defaultValue: "-", example: "json"},
  {name: "gender", type: "Enum", desc: "사용자 성별", required: false, defaultValue: "ALL", example: "M"},
  {name: "age", type: "Integer", desc: "사용자 만 나이", required: false, defaultValue: "-", example: "25"},
];

const REQUEST_VALUE_RULES = [
  {name: "output", values: "xml, json", desc: "원하는 API 응답 형식을 입력합니다."},
  {name: "gender", values: "M, F, ALL", desc: "M은 남자, F는 여자, ALL은 전체를 의미합니다. 미입력 시 ALL로 처리됩니다."},
  {
    name: "age",
    values: "정수",
    desc: "사용자의 실제 만 나이를 입력합니다.",
    ranges: ["10~19: 10대", "20~29: 20대", "30~39: 30대", "40~49: 40대", "50~59: 50대", "60 이상: 60대 이상"],
  },
];

const VAST_FIELDS = [
  {group: "루트 노드", tag: "<VAST version>", desc: "VAST 규격 버전 명시", required: true, example: "4.1, 4.2"},
  {group: "광고 단위", tag: "<Ad id>", desc: "광고 식별자 (서버 내부 광고 ID)", required: true, example: "12345"},
  {group: "", tag: "<InLine>", desc: "광고 메타 및 소재 정보 직접 포함 시 사용", required: true, requiredLabel: "필수 (또는 <Wrapper>)", example: "—"},
  {group: "", tag: "<Wrapper>", desc: "다른 VAST XML로 리디렉션 시 사용", required: false, example: "—"},
  {group: "광고 시스템 정보", tag: "<AdSystem>", desc: "광고 송출 시스템 이름 및 버전", required: true, example: "MyAdServer v1.0"},
  {group: "", tag: "<AdTitle>", desc: "광고 제목", required: true, example: "Summer Campaign 2025"},
  {group: "노출 트래킹", tag: "<Impression>", desc: "광고 노출(재생 전) 트래킹 URL", required: true, example: "https://track.example.com/imp?id=12345"},
  {group: "크리에이티브 묶음", tag: "<Creatives>", desc: "광고 소재 묶음 노드", required: true, example: "—"},
  {group: "", tag: "<Creative>", desc: "개별 광고 크리에이티브 단위", required: true, example: "—"},
  {group: "선형 광고", tag: "<Linear>", desc: "비디오/오디오 스트림 형태 광고 정의", required: true, example: "—"},
  {group: "", tag: "<Duration>", desc: "광고 길이", required: true, example: "00:00:30"},
  {group: "", tag: "<MediaFiles>", desc: "실제 광고 미디어 파일 리스트", required: true, example: "—"},
  {group: "", tag: "<MediaFile>", desc: "광고 영상/음성 파일 URL", required: true, example: "<MediaFile type=\"audio/mpeg\">https://cdn.example.com/ad.mp3</MediaFile>"},
  {group: "", tag: "속성", desc: "type: MIME 타입, bitrate, delivery 등", required: true, example: "type=\"audio/mpeg\" bitrate=\"160\" delivery=\"progressive\""},
  {group: "트래킹 이벤트", tag: "<TrackingEvents>", desc: "광고 재생 중 이벤트 트래킹 URL 모음", required: false, example: "—"},
  {group: "", tag: "<Tracking event=\"start\">", desc: "광고 시작 시 호출", required: false, example: "https://track.example.com/start?id=12345"},
  {group: "", tag: "<Tracking event=\"midpoint\">", desc: "중간(50%) 시 호출", required: false, example: "https://track.example.com/mid?id=12345"},
  {group: "", tag: "<Tracking event=\"complete\">", desc: "완료 시 호출", required: false, example: "https://track.example.com/complete?id=12345"},
  {group: "클릭 정보", tag: "<VideoClicks>", desc: "클릭 관련 정보 노드", required: false, example: "—"},
  {group: "", tag: "<ClickThrough>", desc: "클릭 시 랜딩 URL", required: false, example: "https://brand.example.com/landing"},
  {group: "컴패니언 배너", tag: "<CompanionAds>", desc: "오디오/비디오 광고 동반 배너", required: false, example: "—"},
  {group: "", tag: "<Companion>", desc: "배너 개별 정의", required: false, example: "<Companion width=\"300\" height=\"250\">…</Companion>"},
  {group: "종료 태그", tag: "</InLine>, </Ad>, </VAST>", desc: "각 노드 종료 태그", required: true, example: "—"},
];

const JSON_FIELDS = [
  {tag: "duration", desc: "오디오 재생 시간", required: true},
  {tag: "impression", desc: "노출수 URL", required: true},
  {tag: "mediaFile", desc: "오디오 파일 경로", required: true},
];

const VAST_SUMMARY = [
  {tag: "<VAST version=\"x.x\">", desc: "VAST 버전 지정"},
  {tag: "<Ad id=\"...\">", desc: "광고 식별자"},
  {tag: "<InLine> / <Wrapper>", desc: "광고가 직접 포함되는지, 또는 다른 태그로 리디렉션되는지"},
  {tag: "<Creatives> → <Creative> → <Linear>", desc: "선형 광고 (오디오도 선형 광고 형태)"},
  {tag: "<Duration>", desc: "광고 지속 시간 (예: 00:00:30)"},
  {tag: "<MediaFiles> → <MediaFile>", desc: "미디어 파일 URL, MIME 타입 (예: audio/mpeg)"},
  {tag: "<Impression>", desc: "노출 추적 URL"},
  {tag: "<TrackingEvents>", desc: "재생 시작, 중간 지점, 완료 등 이벤트 추적 URL"},
  {tag: "<CompanionAds> (선택)", desc: "오디오 광고와 함께 제공할 배너 등 시각 광고"},
];

function CollapsibleSection({title, desc, defaultOpen = true, children}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Section>
      <SectionHeader onClick={() => setOpen(prev => !prev)} $open={open}>
        <SectionHeaderText>
          <SectionTitle>{title}</SectionTitle>
          {desc && <SectionDesc>{desc}</SectionDesc>}
        </SectionHeaderText>
        <SectionChevron $open={open}>▾</SectionChevron>
      </SectionHeader>
      {open && <SectionBody>{children}</SectionBody>}
    </Section>
  );
}

export default function ApiGuide() {
  return (
    <main>
      <BoardContainer>
        <TitleContainer>
          <div>
            <h1>API 연동 가이드</h1>
          </div>
          <TitleActions>
            <TestPageLink to="/api-guide/test">API 테스트해보기</TestPageLink>
            <SourceLink href={NOTION_GUIDE_URL} target="_blank" rel="noopener noreferrer">
              원본 문서 보기 ↗
            </SourceLink>
          </TitleActions>
        </TitleContainer>

        <Lead>매체 지면에서 오디오 광고를 수신하기 위한 광고 송출 API 요청/응답 규격을 안내합니다.</Lead>

        <CollapsibleSection title="1. Request" desc="광고 요청 시 사용하는 도메인과 파라미터입니다.">
            <MetaRow>
              <MetaLabel>Domain</MetaLabel>
              <code>http://dsp.oaple.co.kr</code>
            </MetaRow>
            <MetaRow>
              <MetaLabel>API URI</MetaLabel>
              <code>/audio/ad</code>
            </MetaRow>

            <FieldTable>
              <thead>
                <tr>
                  <th>파라미터</th>
                  <th>타입</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                  <th>기본값</th>
                  <th>예시</th>
                </tr>
              </thead>
              <tbody>
                {REQUEST_PARAMS.map(param => (
                  <tr key={param.name}>
                    <td><code>{param.name}</code></td>
                    <td>{param.type}</td>
                    <td>{param.desc}</td>
                    <td>{param.required ? <RequiredBadge>필수</RequiredBadge> : <OptionalBadge>선택</OptionalBadge>}</td>
                    <td><code>{param.defaultValue}</code></td>
                    <td>{param.example}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ParameterRules>
              <ParameterRulesTitle>허용 값 및 입력 규칙</ParameterRulesTitle>
              {REQUEST_VALUE_RULES.map(rule => (
                <ParameterRule key={rule.name}>
                  <ParameterName>{rule.name}</ParameterName>
                  <ParameterValues>{rule.values}</ParameterValues>
                  <ParameterDescription>
                    <span>{rule.desc}</span>
                    {rule.ranges && (
                      <AgeRanges aria-label="연령 분류 구간">
                        {rule.ranges.map(range => <AgeRange key={range}>{range}</AgeRange>)}
                      </AgeRanges>
                    )}
                  </ParameterDescription>
                </ParameterRule>
              ))}
            </ParameterRules>

            <ExampleBlock>
              <ExampleLabel>요청 예시 · 선택 파라미터 포함</ExampleLabel>
              <pre>{"GET http://dsp.oaple.co.kr/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json&gender=M&age=25"}</pre>
            </ExampleBlock>
            <ExampleBlock>
              <ExampleLabel>요청 예시 · 필수 파라미터만 사용</ExampleLabel>
              <pre>{"GET http://dsp.oaple.co.kr/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json"}</pre>
            </ExampleBlock>
        </CollapsibleSection>

        <CollapsibleSection title="2. Response — XML (VAST 규격)" desc="output=xml 요청 시 VAST 4.x 규격의 XML 응답을 반환합니다.">
            <FieldTable>
              <thead>
                <tr>
                  <th>구분</th>
                  <th>태그명 / 필드</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                  <th>예시 값</th>
                </tr>
              </thead>
              <tbody>
                {VAST_FIELDS.map((field, index) => (
                  <tr key={index}>
                    <td>{field.group}</td>
                    <td><code>{field.tag}</code></td>
                    <td>{field.desc}</td>
                    <td>{field.required
                        ? <RequiredBadge>{field.requiredLabel || "필수"}</RequiredBadge>
                        : <OptionalBadge>선택</OptionalBadge>}</td>
                    <td>{field.example}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ExampleBlock>
              <ExampleLabel>예시 · 광고 있을 시</ExampleLabel>
              <pre>{VAST_EXAMPLE}</pre>
            </ExampleBlock>
            <ExampleBlock>
              <ExampleLabel>예시 · 광고 없을 시 (noAd)</ExampleLabel>
              <pre>{VAST_NOAD_EXAMPLE}</pre>
            </ExampleBlock>
            <NoteBox>광고가 없을 경우(noAd)에는 <code>&lt;Ad&gt;</code> 노드가 응답에 포함되지 않습니다.</NoteBox>
        </CollapsibleSection>

        <CollapsibleSection title="3. Response — JSON" desc="output=json 요청 시 간략화된 JSON 응답을 반환합니다.">
            <FieldTable>
              <thead>
                <tr>
                  <th>태그명 / 필드</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                </tr>
              </thead>
              <tbody>
                {JSON_FIELDS.map(field => (
                  <tr key={field.tag}>
                    <td><code>{field.tag}</code></td>
                    <td>{field.desc}</td>
                    <td>{field.required ? <RequiredBadge>필수</RequiredBadge> : <OptionalBadge>선택</OptionalBadge>}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ExampleBlock>
              <ExampleLabel>예시 · 광고 있을 시</ExampleLabel>
              <pre>{JSON_EXAMPLE}</pre>
            </ExampleBlock>
            <ExampleBlock>
              <ExampleLabel>예시 · 광고 없을 시 (noAd)</ExampleLabel>
              <pre>{JSON_NOAD_EXAMPLE}</pre>
            </ExampleBlock>
            <NoteBox>광고가 없을 경우(noAd)에는 <code>response</code> 값이 <code>noAd</code>로 내려오며 <code>ads</code> 값은 비어 있습니다.</NoteBox>
        </CollapsibleSection>

        <CollapsibleSection title="참고 · VAST 사양 개요 (오디오 포함)">
            <RefList>
              <li>VAST 4.1부터 오디오 광고가 표준 사양에 포함되며, <code>adType</code> 속성으로 <code>audio</code>/<code>video</code>/<code>hybrid</code>를 지정할 수 있습니다.</li>
              <li>VAST 4.2에서는 오디오/비디오 병합, 서버 사이드 삽입(SSAI) 등 기능이 강화되었습니다.</li>
              <li>VAST 응답은 XML 구조이며 <code>&lt;Ad&gt;</code>, <code>&lt;Creatives&gt;</code>, <code>&lt;MediaFiles&gt;</code> 등의 요소를 포함합니다.</li>
              <li>오디오 광고 전용 DAAST 사양이 과거 별도로 존재했으나, VAST 4.1 이후로는 VAST에 통합되는 방향입니다.</li>
              <li>출처: IAB Tech Lab (VAST 4.1 / 4.2 공식 문서)</li>
            </RefList>

            <SubTitle>VAST XML 구조 내 주요 요소</SubTitle>
            <FieldTable>
              <thead>
                <tr>
                  <th>태그</th>
                  <th>설명</th>
                </tr>
              </thead>
              <tbody>
                {VAST_SUMMARY.map(item => (
                  <tr key={item.tag}>
                    <td><code>{item.tag}</code></td>
                    <td>{item.desc}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>
        </CollapsibleSection>
      </BoardContainer>
    </main>
  );
}

const TitleActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--theme-primary-border);
  border-radius: 8px;
  background: var(--theme-primary-soft);
  color: var(--theme-primary-strong);
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: var(--theme-primary-soft-hover);
    border-color: #93c5fd;
  }
`;

const TestPageLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #bbf0d1;
  border-radius: 8px;
  background: #ecfdf5;
  color: #059669;
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #d1fae5;
    border-color: #86efac;
  }
`;

const Lead = styled.p`
  margin: 4px 0 20px;
  color: #666e76;
  font-size: 12px;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, .045);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: ${p => (p.$open ? "1px solid #e9edf1" : "none")};
  background: #fafbfc;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f3f5f7;
  }
`;

const SectionHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionChevron = styled.span`
  flex-shrink: 0;
  color: #8a949f;
  font-size: 13px;
  transform: rotate(${p => (p.$open ? "0deg" : "-90deg")});
  transition: transform .15s ease;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #202a35;
  font-size: 14px;
  font-weight: 750;
`;

const SectionDesc = styled.p`
  margin: 5px 0 0;
  color: #8a949f;
  font-size: 11px;
  line-height: 1.5;
`;

const SectionBody = styled.div`
  padding: 18px 20px 20px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 11px;

  code {
    padding: 3px 8px;
    border-radius: 6px;
    background: #f2f7fd;
    color: var(--theme-primary-strong);
    font-weight: 700;
  }
`;

const MetaLabel = styled.span`
  min-width: 70px;
  color: #67707a;
  font-weight: 700;
`;

const FieldTable = styled.table`
  width: 100%;
  margin: 14px 0;
  border: 1px solid #e4e9ee;
  border-collapse: collapse;
  border-radius: 8px;
  font-size: 11px;
  overflow: hidden;

  th {
    padding: 10px 12px;
    border-bottom: 2px solid #8bb8ea;
    background: #f2f7fd;
    color: #34475a;
    font-weight: 750;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 9px 12px;
    border-bottom: 1px solid #edf1f4;
    color: #46515d;
    vertical-align: top;
    word-break: break-all;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  code {
    color: #27313c;
    font-weight: 700;
  }
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--theme-primary-soft);
  color: var(--theme-primary-strong);
  font-size: 9px;
  font-weight: 750;
  white-space: nowrap;
`;

const OptionalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: #6b7684;
  font-size: 9px;
  font-weight: 750;
`;

const ParameterRules = styled.div`
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid #e4e9ee;
  border-radius: 8px;
  background: #fff;
`;

const ParameterRulesTitle = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid #e4e9ee;
  background: #f8fafc;
  color: #34475a;
  font-size: 11px;
  font-weight: 750;
`;

const ParameterRule = styled.div`
  display: grid;
  grid-template-columns: 90px 130px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #edf1f4;
  font-size: 11px;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 720px) {
    grid-template-columns: 80px minmax(0, 1fr);
  }
`;

const ParameterName = styled.code`
  color: #27313c;
  font-weight: 750;
`;

const ParameterValues = styled.code`
  padding: 4px 7px;
  border-radius: 5px;
  background: var(--theme-primary-soft);
  color: var(--theme-primary-strong);
  font-weight: 700;
  white-space: nowrap;
`;

const ParameterDescription = styled.span`
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: #66717d;
  line-height: 1.55;

  @media (max-width: 720px) {
    grid-column: 1 / -1;
  }
`;

const AgeRanges = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const AgeRange = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 3px 8px;
  border: 1px solid var(--theme-primary-border);
  border-radius: 6px;
  background: #fff;
  color: #526170;
  font-size: 10px;
  font-weight: 650;
  white-space: nowrap;
`;

const ExampleBlock = styled.div`
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid #e3e8ee;
  border-radius: 8px;

  pre {
    margin: 0;
    padding: 14px 16px;
    overflow-x: auto;
    background: #1e2530;
    color: #d7e0ea;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 10.5px;
    line-height: 1.6;
    white-space: pre;
  }
`;

const ExampleLabel = styled.div`
  padding: 7px 14px;
  background: #eef2f6;
  color: #4b5563;
  font-size: 10px;
  font-weight: 750;
`;

const NoteBox = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid #fde68a;
  border-radius: 7px;
  background: #fffbeb;
  color: #92400e;
  font-size: 11px;
  line-height: 1.6;

  code {
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(146, 64, 14, .1);
    font-weight: 700;
  }
`;

const RefList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #4a5561;
  font-size: 11px;
  line-height: 1.9;

  code {
    color: #27313c;
    font-weight: 700;
  }
`;

const SubTitle = styled.h3`
  margin: 18px 0 0;
  color: #2d343b;
  font-size: 12px;
  font-weight: 750;
`;
